import { useTranslation } from "react-i18next";
import { Link, useLocation } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import CtaTrustLine from "@/components/shared/CtaTrustLine";
import Reveal from "@/components/motion/Reveal";
import MagneticButton from "@/components/motion/MagneticButton";
import { trackEvent } from "@/lib/track-event";

export type CtaContext = `product.${string}` | `occasion.${string}` | `region.${string}` | "city" | "blog" | "business" | "compare" | "rent" | "locations" | "default";

interface CtaCopy {
  headline: string;
  sub: string;
  button: string;
}

interface CtaBlockProps {
  variant: "mid" | "end";
  context?: CtaContext;
  cityName?: string;
  prefix?: string;
  to?: string;
  headline?: string;
  subline?: string;
  buttonLabel?: string;
  trackingButton?: "mid_cta" | "end_cta";
  className?: string;
  showTrust?: boolean;
}

const CtaBlock = ({
  variant,
  context = "default",
  cityName,
  prefix,
  to = "/jetzt-anfragen",
  headline,
  subline,
  buttonLabel,
  trackingButton,
  className = "",
  showTrust = true,
}: CtaBlockProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const langPrefix = prefix ?? (i18n.language === "en" ? "/en" : "");
  const translated = t(`cta.${context}`, { returnObjects: true, city: cityName }) as CtaCopy;
  const fallback = t("cta.default", { returnObjects: true }) as CtaCopy;
  const copy = typeof translated === "object" ? translated : fallback;
  const heading = headline ?? copy.headline;
  const sub = subline ?? copy.sub;
  const label = buttonLabel ?? copy.button;
  const button = trackingButton ?? (variant === "mid" ? "mid_cta" : "end_cta");
  const isCity = context === "city";

  const content = (
    <>
      <h2 className={`font-heading font-bold leading-tight ${variant === "end" ? "text-2xl sm:text-3xl text-surface-night-foreground" : "text-xl lg:text-2xl text-foreground"}`}>
        {isCity ? <span className="notranslate" translate="no">{heading}</span> : heading}
      </h2>
      {sub ? (
        <p className={`mt-3 text-sm sm:text-base ${variant === "end" ? "text-surface-night-muted" : "text-muted-foreground"}`}>
          {isCity ? <span className="notranslate" translate="no">{sub}</span> : sub}
        </p>
      ) : null}
      <MagneticButton className="mt-6 inline-block w-full sm:w-auto">
        <Button asChild className="h-auto w-full whitespace-normal bg-cta px-7 py-3 text-base font-semibold text-cta-foreground shadow-md hover:bg-cta-hover sm:w-auto">
          <Link
            to={`${langPrefix}${to}`}
            onClick={() => trackEvent("cta_click", { button, context, page: location.pathname })}
          >
            {isCity ? <span className="notranslate" translate="no">{label}</span> : label}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </MagneticButton>
      {showTrust ? <CtaTrustLine prefix={langPrefix} className={variant === "end" ? "text-surface-night-muted" : ""} /> : null}
    </>
  );

  if (variant === "end") {
    return (
      <section className={`section-night py-16 lg:py-24 ${className}`} data-cta-primary={button} data-cta-context={context} data-cta-label={label}>
        <Reveal className="container mx-auto max-w-3xl px-4 text-center lg:px-8">{content}</Reveal>
      </section>
    );
  }

  return (
    <section className={`py-10 lg:py-14 ${className}`} data-cta-primary={button} data-cta-context={context} data-cta-label={label}>
      <Reveal className="container mx-auto max-w-3xl px-4 lg:px-8">
        <div className="rounded-lg border border-border bg-card px-6 py-8 text-center shadow-sm lg:px-10 lg:py-10">{content}</div>
      </Reveal>
    </section>
  );
};

export default CtaBlock;